import { Button, Tooltip, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Notification } from '@hero/atoms';
import { SkeletonLoaders } from '@hero/components';
import { DuplicateModal, TotalEstimatedSpendModal } from '@hero/components/modals';
import { CARDISLE_GREETING_CARD } from '@hero/constants';
import { formatCurrency, getAddonData } from '@hero/utils/helper';

import * as S from './style';

function DraftCostSummary({
  isCCPayment,
  estimatedCharges,
  isLoadingSpendingSummary,
  onDuplicate,
  onDelete,
  payAndSendButtonProps,
  featureType,
  feature,
  contactType,
  isError,
  isLoadingFeature
}) {
  const { t } = useTranslation();

  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [isEstimatedModalOpen, setIsEstimatedModalOpen] = useState(false);

  const minEstimatedCharge = estimatedCharges?.serviceCharges?.min;
  const maxEstimatedCharge = estimatedCharges?.serviceCharges?.max;
  const hasMinMaxEstimatedCharge =
    typeof minEstimatedCharge === 'number' && typeof maxEstimatedCharge === 'number';

  const digitalGiftCard = getAddonData(feature?.addons, CARDISLE_GREETING_CARD);

  const getValue = value => {
    const hasEstimatedCharge = !!estimatedCharges?.serviceCharge || !!hasMinMaxEstimatedCharge;
    if (isError) {
      return 'N/A';
    } else if (isLoadingSpendingSummary || !hasEstimatedCharge) {
      return <SkeletonLoaders.CostSummaryLoader count={30} />;
    }
    return formatCurrency(value);
  };

  return (
    <S.CostSummaryCard>
      <Typography as="div" variant="body1Bold" style={{ display: 'flex', gap: 4 }}>
        {t('costSummary')}
        {featureType === 'automation' && (
          <Typography variant="body2" color="fg" emphasis="weak">
            ({t('perRecipient').toLocaleLowerCase()})
          </Typography>
        )}
      </Typography>
      <S.Content>
        <LiveCostSummaryContent
          estimatedCharges={estimatedCharges}
          featureType={featureType}
          isCCPayment={isCCPayment}
          isError={isError}
          isLoadingFeature={isLoadingFeature}
          isLoadingSpendingSummary={isLoadingSpendingSummary}
        />

        <S.TotalRow>
          <Typography as="div" variant="body2Bold">
            {t('totalEstimatedSpend')}:
            <Button
              onClick={() => setIsEstimatedModalOpen(true)}
              variant="link"
              color="neutral"
              style={{ fontSize: 12 }}
            >
              {t('howOurPriceWork')}
            </Button>
          </Typography>
          <Typography style={{ alignSelf: 'flex-start' }} variant="body1ExtraBold">
            {getValue(estimatedCharges?.total)}
          </Typography>
        </S.TotalRow>

        <Tooltip content={payAndSendButtonProps?.content}>
          <Button {...payAndSendButtonProps} block />
        </Tooltip>
        <Typography
          style={{ display: 'inline-flex', gap: 4, alignSelf: 'center', flexWrap: 'wrap' }}
          variant="body2"
          emphasis="500"
        >
          <Trans
            i18nKey="youCanDuplicateOrDeleteX"
            values={{
              feature: featureType.toLocaleLowerCase()
            }}
            components={{
              button1: (
                <Button
                  onClick={() => {
                    setDuplicateModalOpen(true);
                  }}
                  variant="link"
                  color="neutral"
                  emphasis="800"
                />
              ),
              button2: <Button onClick={onDelete} variant="link" color="error" emphasis="800" />
            }}
          />
        </Typography>
      </S.Content>

      <TotalEstimatedSpendModal
        featureType={featureType}
        isCCPayment={isCCPayment}
        isModalOpen={isEstimatedModalOpen}
        onModalOpen={() => setIsEstimatedModalOpen(false)}
      />
      <DuplicateModal
        isOpen={duplicateModalOpen}
        onOpen={setDuplicateModalOpen}
        onSubmit={onDuplicate}
        featureType={featureType}
        duplicateText={feature?.name}
        recipientCount={estimatedCharges?.recipientCount}
        giftCount={feature?.products?.length}
        contactType={contactType}
        giftMessage={feature?.experienceSettings?.messageParams?.message}
        giftPrice={estimatedCharges?.baseProductPrice}
        digitalGiftCard={digitalGiftCard}
      />
    </S.CostSummaryCard>
  );
}

DraftCostSummary.propTypes = {
  isCCPayment: PropTypes.bool,
  estimatedCharges: PropTypes.shape({
    serviceCharge: PropTypes.number,
    total: PropTypes.number
  }),
  isLoadingSpendingSummary: PropTypes.bool,
  onDuplicate: PropTypes.func,
  onDelete: PropTypes.func,
  payAndSendButtonProps: PropTypes.shape({
    content: PropTypes.string
  }),
  featureType: PropTypes.string,
  isError: PropTypes.bool,
  isLoadingFeature: PropTypes.bool,
  feature: PropTypes.shape({
    addons: PropTypes.array,
    name: PropTypes.string,
    products: PropTypes.array,
    experienceSettings: PropTypes.shape({
      messageParams: PropTypes.shape({ message: PropTypes.string })
    })
  }),
  contactType: PropTypes.string
};

function LiveCostSummaryContent({
  estimatedCharges,
  featureType,
  isCCPayment,
  isError,
  isLoadingFeature,
  isLoadingSpendingSummary
}) {
  const { t } = useTranslation();

  const minEstimatedCharge = estimatedCharges?.serviceCharges?.min;
  const maxEstimatedCharge = estimatedCharges?.serviceCharges?.max;
  const hasMinMaxEstimatedCharge =
    typeof minEstimatedCharge === 'number' && typeof maxEstimatedCharge === 'number';
  const shouldShowEstimatedChargeRange = minEstimatedCharge === 0 && maxEstimatedCharge !== 0;

  const getValue = value => {
    const hasEstimatedCharge = !!estimatedCharges?.serviceCharge || !!hasMinMaxEstimatedCharge;
    if (isError) {
      return 'N/A';
    } else if (isLoadingSpendingSummary || !hasEstimatedCharge) {
      return <SkeletonLoaders.CostSummaryLoader count={30} />;
    }
    return formatCurrency(value);
  };

  return (
    <S.LcsContent>
      <S.LcsContentRow>
        <Typography as="div" variant="body2SemiBold">
          {t('itemsEstimate') + ':'}
          <Typography variant="caption1" color="neutral" emphasis="400">
            {featureType === 'automation'
              ? `(${t('costSummarySubTitle1Automation')})`
              : `(${t('costSummarySubTitle1', {
                  recipientCount: estimatedCharges?.recipientCount
                })})`}
          </Typography>
        </Typography>
        <S.LcsContentValue>{getValue(estimatedCharges?.merchandise)}</S.LcsContentValue>
      </S.LcsContentRow>
      <S.LcsContentRow>
        <Typography as="div" variant="body2SemiBold">
          {t('estimatedServiceAndShippingFees') + ':'}
          {shouldShowEstimatedChargeRange && (
            <Typography variant="caption1" color="fg" emphasis="weak">
              {t('serviceChargeMessageForFreeShip')}
            </Typography>
          )}
        </Typography>
        {shouldShowEstimatedChargeRange ? (
          <S.LcsContentValue>
            {getValue(minEstimatedCharge)} - {getValue(maxEstimatedCharge)}
          </S.LcsContentValue>
        ) : (
          <S.LcsContentValue>{getValue(estimatedCharges?.serviceCharge)}</S.LcsContentValue>
        )}
      </S.LcsContentRow>
      <S.LcsContentRow>
        <Typography variant="body2SemiBold">{t('estimatedTax') + ':'}</Typography>
        <S.LcsContentValue>{getValue(estimatedCharges?.estimatedTax)}</S.LcsContentValue>
      </S.LcsContentRow>

      {!isLoadingSpendingSummary && !isLoadingFeature && isCCPayment && (
        <Notification
          style={{ margin: '8px 0' }}
          status="info"
          message={
            <Trans
              i18nKey="yPayThisAmountNowDesc"
              values={{
                amount: formatCurrency(estimatedCharges?.total),
                featureType: t(featureType).toLocaleLowerCase()
              }}
              components={{ b: <b /> }}
            />
          }
        />
      )}

      {featureType === 'automation' && (
        <Notification
          style={{ margin: '8px 0' }}
          status="info"
          isClosable
          message={
            <Trans i18nKey="yPayThisAmountNowDescAutomationText" components={{ b: <b /> }} />
          }
        />
      )}
    </S.LcsContent>
  );
}

LiveCostSummaryContent.propTypes = {
  adjustedEstimatedCharge: PropTypes.shape({
    merchandise: PropTypes.number,
    serviceCharge: PropTypes.number,
    estimatedTax: PropTypes.number
  }),
  estimatedCharges: PropTypes.shape({
    serviceCharges: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number
    }),
    serviceCharge: PropTypes.number,
    recipientCount: PropTypes.number,
    merchandise: PropTypes.number,
    estimatedTax: PropTypes.number,
    total: PropTypes.number
  }),
  featureType: PropTypes.string,
  isCCPayment: PropTypes.bool,
  isError: PropTypes.bool,
  isLoadingFeature: PropTypes.bool,
  isLoadingSpendingSummary: PropTypes.bool
};

export { DraftCostSummary, LiveCostSummaryContent };
